import { Badge } from "@/components/ui/badge";

import type { ImageTriageDto } from "../../types";

const plural = (
  count: number,
  singular: string,
  pluralValue = `${singular}s`,
) => (count === 1 ? singular : pluralValue);

export function ImageTriageSummary({ dto }: { dto: ImageTriageDto }) {
  const summary = dto.summary ?? {};
  const findings = summary.finding_counts ?? {};
  const groups = summary.group_counts ?? {};
  const groupedImages = summary.grouped_image_counts ?? {};
  const duplicateGroups =
    (groups.exact_bytes ?? 0) + (groups.canonical_pixels ?? 0);
  const duplicatePhotos =
    (groupedImages.exact_bytes ?? 0) + (groupedImages.canonical_pixels ?? 0);
  const variantGroups = groups.altered_copy ?? 0;
  const variantPhotos = groupedImages.altered_copy ?? 0;
  const similarGroups = groups.similar_moment ?? 0;
  const similarPhotos = groupedImages.similar_moment ?? 0;
  const chips = [
    duplicateGroups
      ? `${duplicateGroups} strict duplicate ${plural(duplicateGroups, "group")} · ${duplicatePhotos} ${plural(duplicatePhotos, "photo")}`
      : null,
    variantGroups
      ? `${variantGroups} altered/annotated ${plural(variantGroups, "variant")} · ${variantPhotos} ${plural(variantPhotos, "photo")}`
      : null,
    similarGroups
      ? `${similarGroups} similar-moment ${plural(similarGroups, "group")} · ${similarPhotos} ${plural(similarPhotos, "photo")}`
      : null,
    findings.resolution_critical
      ? `${findings.resolution_critical} very low resolution`
      : null,
    findings.resolution_watch
      ? `${findings.resolution_watch} resolution watch`
      : null,
    findings.focus_likely_blur
      ? `${findings.focus_likely_blur} likely blurry`
      : null,
    findings.focus_watch ? `${findings.focus_watch} focus review` : null,
    findings.possible_screenshot
      ? `${findings.possible_screenshot} possible ${plural(findings.possible_screenshot, "screenshot")}`
      : null,
    summary.failed_images ? `${summary.failed_images} not analyzed` : null,
  ].filter((value): value is string => Boolean(value));

  return (
    <div className="space-y-2">
      {chips.length ? (
        <div
          className="flex flex-wrap gap-2"
          aria-label="Image preprocessing summary"
        >
          {chips.map((chip) => (
            <Badge
              key={chip}
              variant="outline"
              className="whitespace-normal text-left"
            >
              {chip}
            </Badge>
          ))}
        </div>
      ) : dto.state === "complete" ? (
        <p className="text-sm text-muted-foreground">
          No configured image-preprocessing signals were triggered.
        </p>
      ) : null}
      {summary.excluded_videos ? (
        <p className="text-xs text-muted-foreground">
          {summary.excluded_videos} {plural(summary.excluded_videos, "video")}{" "}
          {summary.excluded_videos === 1 ? "was" : "were"} not part of image
          preprocessing.
        </p>
      ) : null}
    </div>
  );
}
